import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { aaiApi } from '../../services/aaiApi';
import type { ControlEvent } from '../../types/aai';
import { Action, Chip, Drawer, Field, Input, Kicker, Select } from '../../components/aa';
import { relativeTime } from '../partner/format';

/*
 * Targeted support: prepare a clinic for the shared blocker. Aggregate support
 * demand helps a role group without exposing individuals or private practice.
 * The clinic is recorded on the team path and reaches the program manager; the
 * request names the department, the blocker and a slot, never a person. The
 * readiness numbers do not change because a clinic was prepared.
 */

const BLOCKERS = [
  'Checking evidence before use',
  'Writing the first prompt for defined work',
  'Knowing when to escalate to a person',
  'Understanding which sources are approved',
  'Reviewing a run before sharing the result',
];

const slotOptions = (): string[] => {
  const out: string[] = [];
  const at = new Date();
  while (out.length < 6) {
    at.setDate(at.getDate() + 1);
    const day = at.getDay();
    if (day === 0 || day === 6) continue;
    const label = at.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
    out.push(`${label} · 10:00 AM`);
    if (out.length < 6) out.push(`${label} · 2:00 PM`);
  }
  return out;
};

export const SupportRequestDrawer: React.FC<{
  department: string;
  affectedWork: string | null;
  people: number | null;
  minGroupSize: number;
  path: string;
  latest: ControlEvent | null;
  onClose: () => void;
  onRecorded: () => void;
}> = ({ department, affectedWork, people, minGroupSize, path, latest, onClose, onRecorded }) => {
  const slots = slotOptions();
  const [blocker, setBlocker] = useState(BLOCKERS[0]);
  const [slot, setSlot] = useState(slots[0]);
  const [owner, setOwner] = useState(localStorage.getItem('user_name') || '');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<ControlEvent | null>(null);

  const prepare = async () => {
    if (!owner.trim()) {
      toast.error('Name the support owner. The clinic needs one accountable person.');
      return;
    }
    setBusy(true);
    try {
      const res = await aaiApi.requestDepartmentSupport({
        subject: `${blocker} · ${department}`,
        note: `Clinic slot: ${slot}. Support owner: ${owner.trim()}. Affected work: ${affectedWork || 'not linked'}. Audience: ${department} roles.`,
        path,
      });
      setDone(res.data);
      toast.success('Clinic prepared. The program manager has the request.');
      onRecorded();
    } catch {
      toast.error('That did not record.');
    } finally {
      setBusy(false);
    }
  };

  const recorded = done || latest;
  let badge = <Chip>Draft</Chip>;
  if (recorded) badge = <Chip tone="warn" dot>Requested {relativeTime(recorded.created_at)}</Chip>;
  let audience = `${department} roles`;
  if (people !== null && people >= minGroupSize) audience = `${people} people in ${department} roles`;

  return (
    <Drawer
      onClose={onClose}
      kicker={<Kicker>Targeted support</Kicker>}
      badge={badge}
      title="Prepare a clinic for the shared blocker"
      sub="Use aggregate support demand to help a role group without exposing individuals or private practice."
      footer={(
        <div className="flex flex-wrap items-center justify-end gap-3">
          <Action size="lg" variant="quiet" onClick={onClose}>Cancel</Action>
          {!done && <Action size="lg" onClick={prepare} disabled={busy} reason="Recording the clinic">Prepare clinic</Action>}
        </div>
      )}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        {[
          { label: 'Affected work', value: affectedWork || 'No linked Project' },
          { label: 'Audience', value: audience },
          { label: 'Privacy threshold', value: `Groups of ${minGroupSize} or more` },
          { label: 'Production data', value: 'Not used' },
        ].map((fact) => (
          <div key={fact.label} className="rounded-card border border-line bg-canvas-inset px-5 py-4">
            <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">{fact.label}</div>
            <div className="mt-1.5 text-[15px] font-bold text-ink">{fact.value}</div>
          </div>
        ))}
      </div>

      <div className="mt-6 space-y-5">
        <Field label="Shared blocker" htmlFor="clinic-blocker">
          <Select id="clinic-blocker" value={blocker} onChange={(e) => setBlocker(e.target.value)} disabled={Boolean(done)}>
            {BLOCKERS.map((b) => <option key={b} value={b}>{b}</option>)}
          </Select>
        </Field>
        <Field label="Clinic slot" htmlFor="clinic-slot">
          <Select id="clinic-slot" value={slot} onChange={(e) => setSlot(e.target.value)} disabled={Boolean(done)}>
            {slots.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </Field>
        <Field label="Support owner" htmlFor="clinic-owner">
          <Input id="clinic-owner" value={owner} onChange={(e) => setOwner(e.target.value)} disabled={Boolean(done)} placeholder="Who runs the clinic" />
        </Field>
      </div>

      <p className="m-0 mt-5 text-[12.5px] leading-[20px] text-ink-dim">
        Preparing the clinic records the request and notifies the program manager. Readiness on this page changes only once the session has happened and its evidence is reviewed.
      </p>
    </Drawer>
  );
};
